import { gql } from './graphql';

/** A yard spot or dock door, with the zone it belongs to. */
export interface FacilityLocation {
  kind: 'yardSpot' | 'dock';
  name: string | null;
  /** The facility zone for a yard spot, the warehouse zone for a dock. */
  zone: string | null;
}

/** Every yard spot and dock at a facility, by uuid. */
export type FacilityLocations = Map<string, FacilityLocation>;

interface NamedRow {
  uuid: string;
  name: string | null;
}

interface YardLayoutZone extends NamedRow {
  yardSpots: NamedRow[] | null;
}

interface WarehouseLayoutZone extends NamedRow {
  docks: NamedRow[] | null;
}

const FACILITY_LOCATIONS = `
  query FacilityLocations($facilityId: ID!) {
    yardLayout(facilityId: $facilityId) {
      uuid
      name
      yardSpots {
        uuid
        name
      }
    }
    warehouseLayout(facilityId: $facilityId) {
      uuid
      name
      docks {
        uuid
        name
      }
    }
  }
`;

const locationsCache = new Map<number, Promise<FacilityLocations>>();

/**
 * The names of every yard spot (with its facility zone) and dock (with its
 * warehouse zone) at a facility, from flux's yard and warehouse layouts.
 * Cached per facility; a failed load is not cached, so the next call retries.
 */
export function loadFacilityLocations(facilityId: number): Promise<FacilityLocations> {
  let cached = locationsCache.get(facilityId);
  if (!cached) {
    cached = gql<{
      yardLayout: YardLayoutZone[] | null;
      warehouseLayout: WarehouseLayoutZone[] | null;
    }>(FACILITY_LOCATIONS, { facilityId }).then((data) => {
      const locations: FacilityLocations = new Map();
      for (const zone of data.yardLayout ?? []) {
        for (const spot of zone.yardSpots ?? []) {
          locations.set(spot.uuid, { kind: 'yardSpot', name: spot.name, zone: zone.name });
        }
      }
      for (const zone of data.warehouseLayout ?? []) {
        for (const dock of zone.docks ?? []) {
          locations.set(dock.uuid, { kind: 'dock', name: dock.name, zone: zone.name });
        }
      }
      return locations;
    });
    cached.catch(() => locationsCache.delete(facilityId));
    locationsCache.set(facilityId, cached);
  }
  return cached;
}
